import { api } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  AddCreditPayload,
  AddCreditResponse,
  AssignUserToAccountResponse,
  Credit,
  Debit,
  FetchAccountDetailsParams,
  FetchAccountDetailsResponse,
  FetchAccountStatusParams,
  FetchAccountStatusResponse,
  FetchAssignedUsersParams,
  FetchAssignedUsersResponse,
  FetchGetSelfResponse,
  FetchStatsResponse,
  FetchSubAccountsParams,
  FetchSubAccountsResponse,
  UnassignUserFromAccountResponse,
} from './types';

const _fetchAccountStatus = async ({
  accountNumber,
}: FetchAccountStatusParams): Promise<AxiosResponse<FetchAccountStatusResponse>> => {
  return api.get(`accounts/${accountNumber}/status`);
};
const useFetchAccountStatus = (
  accountNumber?: FetchAccountStatusParams['accountNumber'],
  enabled?: boolean
) => {
  return useQuery({
    queryKey: ['fetch-account-status', { accountNumber }],
    queryFn: () => _fetchAccountStatus({ accountNumber: accountNumber! }),
    enabled: !!accountNumber && (!!enabled || typeof enabled === 'undefined'),
    retry: 0,
  });
};

const _fetchSelf = async (): Promise<AxiosResponse<FetchGetSelfResponse>> => {
  return api.get('users/getself', {
    withStraticsUiHeader: true,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

const useFetchSelf = (enabled?: boolean) => {
  return useQuery({
    queryKey: ['fetch-self'],
    queryFn: _fetchSelf,
    retry: 0,
    enabled,
  });
};

const _fetchAccountDetails = async ({
  accountNumber,
}: FetchAccountDetailsParams): Promise<AxiosResponse<FetchAccountDetailsResponse>> => {
  return api.get(`accounts/${accountNumber}`);
};

const useFetchAccountDetails = (
  accountNumber?: FetchAccountDetailsParams['accountNumber'],
  enabled?: boolean
) => {
  return useQuery({
    queryKey: ['fetch-account-details', { accountNumber }],
    queryFn: () => _fetchAccountDetails({ accountNumber: accountNumber! }),
    enabled: !!accountNumber && (!!enabled || typeof enabled === 'undefined'),
    retry: 0,
  });
};

const _fetchStats = async (): Promise<AxiosResponse<FetchStatsResponse>> => {
  return api.get('aggregatestats.json');
};
const useFetchStats = (enabled?: boolean) => {
  return useQuery({
    queryKey: ['fetch-stats'],
    queryFn: _fetchStats,
    enabled: !!enabled || typeof enabled === 'undefined',
    retry: 0,
    refetchInterval: 10000,
    staleTime: 0, // Always refetch for polling
  });
};

const _fetchSubAccounts = async ({
  rootAccountId,
}: FetchSubAccountsParams): Promise<AxiosResponse<FetchSubAccountsResponse>> => {
  return api.get(`accounts/${rootAccountId}/subaccounts`);
};

const useFetchSubAccounts = (
  rootAccountId?: FetchSubAccountsParams['rootAccountId'],
  enabled?: boolean
) => {
  return useQuery({
    queryKey: ['fetch-sub-accounts', { rootAccountId }],
    queryFn: () => _fetchSubAccounts({ rootAccountId: rootAccountId! }),
    enabled: !!rootAccountId && (!!enabled || typeof enabled === 'undefined'),
    retry: 0,
  });
};

const _fetchAssignedUsers = async ({
  accountId,
}: FetchAssignedUsersParams): Promise<AxiosResponse<FetchAssignedUsersResponse>> => {
  return api.get(`accounts/${accountId}/users`);
};

const useFetchAssignedUsers = (
  accountId?: FetchAssignedUsersParams['accountId'],
  enabled?: boolean
) => {
  return useQuery({
    queryKey: ['fetch-assigned-users', { accountId }],
    queryFn: () => _fetchAssignedUsers({ accountId: accountId! }),
    enabled: !!accountId && (!!enabled || typeof enabled === 'undefined'),
    retry: 0,
  });
};

// Account Update API
const _updateAccount = async ({
  accountId,
  accountData,
}: {
  accountId: string;
  accountData: Partial<FetchAccountDetailsResponse>;
}): Promise<AxiosResponse<FetchAccountDetailsResponse>> => {
  return api.put(`accounts/${accountId}`, accountData);
};

const useUpdateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: _updateAccount,
    onSuccess: (data, variables) => {
      // Invalidate and refetch account details
      queryClient.invalidateQueries({
        queryKey: ['fetch-account-details', { accountNumber: variables.accountId }],
      });

      // Update the cache with the new data
      queryClient.setQueryData(
        ['fetch-account-details', { accountNumber: variables.accountId }],
        data
      );
    },
  });
};

// User Assignment API
const _assignUserToAccount = async ({
  userId,
  accountId,
}: {
  userId: string;
  accountId: string;
}): Promise<AxiosResponse<AssignUserToAccountResponse>> => {
  return api.post(`users/${userId}/assignaccount`, { account_id: accountId });
};

const useAssignUserToAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: _assignUserToAccount,
    onSuccess: (data, variables) => {
      // Invalidate assigned users query to refetch the list
      queryClient.invalidateQueries({
        queryKey: ['fetch-assigned-users', { accountId: variables.accountId }],
      });
    },
  });
};

const _unassignUserFromAccount = async ({
  userId,
  accountId,
}: {
  userId: string;
  accountId: string;
}): Promise<AxiosResponse<UnassignUserFromAccountResponse>> => {
  return api.post(`users/${userId}/unassignaccount`, { account_id: accountId });
};

const useUnassignUserFromAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: _unassignUserFromAccount,
    onSuccess: (data, variables) => {
      // Invalidate assigned users query to refetch the list
      queryClient.invalidateQueries({
        queryKey: ['fetch-assigned-users', { accountId: variables.accountId }],
      });
    },
  });
};

// Fetch account credits
const _fetchAccountCredits = async ({
  accountId,
}: {
  accountId: string;
}): Promise<AxiosResponse<Credit[]>> => {
  return api.get(`accounts/${accountId}/credits`);
};

const useFetchAccountCredits = (accountId?: string, enabled?: boolean) => {
  return useQuery({
    queryKey: ['fetch-account-credits', { accountId }],
    queryFn: () => _fetchAccountCredits({ accountId: accountId! }),
    enabled: !!accountId && (!!enabled || typeof enabled === 'undefined'),
    retry: 0,
  });
};

// Fetch account debits
const _fetchAccountDebits = async ({
  accountId,
}: {
  accountId: string;
}): Promise<AxiosResponse<Debit[]>> => {
  return api.get(`accounts/${accountId}/debits`);
};

const useFetchAccountDebits = (accountId?: string, enabled?: boolean) => {
  return useQuery({
    queryKey: ['fetch-account-debits', { accountId }],
    queryFn: () => _fetchAccountDebits({ accountId: accountId! }),
    enabled: !!accountId && (!!enabled || typeof enabled === 'undefined'),
    retry: 0,
  });
};

// Add credit to account
const _addAccountCredit = async ({
  accountId,
  payload,
}: {
  accountId: string;
  payload: Omit<AddCreditPayload, 'account_id'>;
}): Promise<AxiosResponse<AddCreditResponse>> => {
  return api.post(`accounts/${accountId}/addcredit`, {
    account_id: accountId,
    ...payload,
  });
};

const useAddAccountCredit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: _addAccountCredit,
    onSuccess: () => {
      // Invalidate both credits and debits to refresh the transactions table
      queryClient.invalidateQueries({ queryKey: ['fetch-account-credits'] });
      queryClient.invalidateQueries({ queryKey: ['fetch-account-debits'] });
      queryClient.invalidateQueries({ queryKey: ['fetch-account-status'] });
    },
  });
};

export {
  useAddAccountCredit,
  useAssignUserToAccount,
  useFetchAccountCredits,
  useFetchAccountDebits,
  useFetchAccountDetails,
  useFetchAccountStatus,
  useFetchAssignedUsers,
  useFetchSelf,
  useFetchStats,
  useFetchSubAccounts,
  useUnassignUserFromAccount,
  useUpdateAccount,
};
